/**
 * Authentication Security Module - Advanced auth protections for generated apps.
 */

export interface AuthSecurityConfig {
  twoFactorEnabled: boolean;
  biometricEnabled: boolean;
  emailVerificationRequired: boolean;
  sessionTimeoutMinutes: number;
  maxLoginAttempts: number;
  lockoutDurationMinutes: number;
  ipMonitoring: boolean;
  loginAnomalyDetection: boolean;
  passwordPolicy: {
    minLength: number;
    requireUppercase: boolean;
    requireNumber: boolean;
    requireSymbol: boolean;
  };
}

export const defaultAuthSecurityConfig: AuthSecurityConfig = {
  twoFactorEnabled: false,
  biometricEnabled: false,
  emailVerificationRequired: true,
  sessionTimeoutMinutes: 60,
  maxLoginAttempts: 5,
  lockoutDurationMinutes: 15,
  ipMonitoring: false,
  loginAnomalyDetection: false,
  passwordPolicy: {
    minLength: 8,
    requireUppercase: true,
    requireNumber: true,
    requireSymbol: false,
  },
};

export function evaluateAuthSecurity(config: AuthSecurityConfig): { score: number; recommendations: string[] } {
  let score = 40;
  const recs: string[] = [];

  if (config.twoFactorEnabled) score += 15; else recs.push("Enable two-factor authentication for stronger account protection.");
  if (config.biometricEnabled) score += 10; else recs.push("Enable WebAuthn/biometric login for passwordless security.");
  if (config.emailVerificationRequired) score += 10; else recs.push("Require email verification to prevent fake accounts.");
  if (config.sessionTimeoutMinutes <= 60) score += 5; else recs.push("Reduce session timeout to 60 minutes or less.");
  if (config.maxLoginAttempts <= 5) score += 5; else recs.push("Limit login attempts to prevent brute force attacks.");
  if (config.ipMonitoring) score += 5; else recs.push("Enable IP monitoring for suspicious login detection.");
  if (config.loginAnomalyDetection) score += 5; else recs.push("Enable login anomaly detection for unknown devices/locations.");
  if (config.passwordPolicy.minLength >= 8) score += 3;
  if (config.passwordPolicy.requireUppercase && config.passwordPolicy.requireNumber) score += 2;

  return { score: Math.min(100, score), recommendations: recs };
}

/**
 * Admin Security - Platform owner security checks.
 */
export interface AdminSecurityStatus {
  twoFactorActive: boolean;
  biometricActive: boolean;
  trustedDevices: number;
  activeSessions: number;
  lastLogin: number;
  suspiciousAlerts: number;
}

export function getDefaultAdminStatus(): AdminSecurityStatus {
  return {
    twoFactorActive: true,
    biometricActive: false,
    trustedDevices: 1,
    activeSessions: 1,
    lastLogin: Date.now(),
    suspiciousAlerts: 0,
  };
}
